import React, { useEffect, useState, useRef } from 'react';

import { DataGrid } from '@mui/x-data-grid';
import { debounce, isEmpty, isEqual, isNull } from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { CustomPagination } from 'common/components';
import BulkOperationsCard from 'common/components/BulkOperationsCard';
import { DEBOUNCE_TIME_500, TABLE_PAGE_SIZE_OPTIONS } from 'common/constants';

import ProductColumns from './ProductColumns';

const ListItems = ({
    filterData,
    filterItems,
    handleClickBulkItems,
    handleClickDuplicate,
    handleClickEdit,
    handleSaveRow,
    handleUpdateFilterData,
    isFetching,
    items,
    paginationData,
    setFilterData,
    systemTypes,
}) => {
    const { t } = useTranslation();
    const [rowCount, setRowCount] = useState(0);
    const [rowModesModel, setRowModesModel] = useState({});
    const [selectedBulkOption, setSelectedBulkOption] = useState('');
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [selectionModel, setSelectionModel] = useState([]);
    const [currentFilterData, setCurrentFilterData] = useState(filterData);

    const handleOnFilter = useRef(
        debounce((filter) => filterItems(filter), DEBOUNCE_TIME_500),
    ).current;

    const handleOnChangeSearch = (event) =>
        setFilterData((prev) => ({
            ...prev,
            page: 0,
            searchText: event.target.value,
        }));

    const handleOnSelectStatus = (status) =>
        setFilterData((prev) => ({ ...prev, page: 0, status }));

    const pageSizeOptions = TABLE_PAGE_SIZE_OPTIONS;

    const columns = ProductColumns({
        filterData,
        handleClickBulkItems,
        handleClickDuplicate,
        handleClickEdit,
        rowModesModel,
        selectedRowId,
        setRowModesModel,
        setSelectedRowId,
        systemTypes,
    });
    const getRowHeight = () => 'auto';
    const initialState = { pagination: { pageSize: filterData?.pageSize } };
    const onPaginationModelChange = (model) =>
        setFilterData((prev) => ({ ...prev, ...model }));
    const onRowEditStart = (params, event) => {
        const id = params.row.id;
        if (isNull(selectedRowId)) setSelectedRowId(id);
        else event.defaultMuiPrevented = true;
        return false;
    };
    const onRowEditStop = (_, event) => {
        event.defaultMuiPrevented = true;
    };
    const onRowModesModelChange = (model) => setRowModesModel(model);
    const onRowSelectionModelChange = (model) => setSelectionModel(model);
    const onSortModelChange = (model) => {
        if (!model[0]) return;
        setFilterData((prev) => ({
            ...prev,
            orderBy: model[0].field.toLocaleLowerCase(),
            sortBy: model[0].sort,
        }));
    };
    const paginationModel = {
        page: paginationData?.current_page - 1 || 0,
        pageSize: filterData?.pageSize || pageSizeOptions[0],
    };
    const processRowUpdate = (newRow) => {
        handleSaveRow(filterData, newRow);
        return newRow;
    };
    const rows = items.map((item) => ({
        ...item,
        system_value: item?.system?.id,
    }));
    const slots = { pagination: CustomPagination };
    const slotProps = { pagination: { pageSizeOptions } };

    useEffect(() => {
        if (isEmpty(paginationData)) return;
        setRowCount((prevRowCount) =>
            paginationData?.total_rows !== undefined
                ? paginationData?.total_rows
                : prevRowCount,
        );
    }, [paginationData?.total_rows]);

    useEffect(() => {
        handleUpdateFilterData(filterData);
        if (isEqual(currentFilterData, filterData)) return;
        handleOnFilter(filterData);
        setCurrentFilterData(filterData);
    }, [filterData]);

    return (
        <>
            <BulkOperationsCard
                filterData={filterData}
                handleClickBulkItems={handleClickBulkItems}
                handleOnChangeSearch={handleOnChangeSearch}
                handleOnSelectStatus={handleOnSelectStatus}
                prefix={t('Product', {
                    count: selectionModel.length,
                }).toLocaleLowerCase()}
                selectedBulkOption={selectedBulkOption}
                selectionModel={selectionModel}
                setSelectedBulkOption={setSelectedBulkOption}
                setSelectionModel={setSelectionModel}
            />

            <DataGrid
                checkboxSelection={true}
                columns={columns}
                disableColumnMenu
                disableRowSelectionOnClick
                editMode="row"
                getRowHeight={getRowHeight}
                hideFooterSelectedRowCount
                initialState={initialState}
                loading={isFetching}
                onPaginationModelChange={onPaginationModelChange}
                onRowEditStart={onRowEditStart}
                onRowEditStop={onRowEditStop}
                onRowModesModelChange={onRowModesModelChange}
                onRowSelectionModelChange={onRowSelectionModelChange}
                onSortModelChange={onSortModelChange}
                pageSizeOptions={pageSizeOptions}
                pagination
                paginationMode="server"
                paginationModel={paginationModel}
                processRowUpdate={processRowUpdate}
                rowCount={rowCount}
                rowModesModel={rowModesModel}
                rows={rows}
                selectionModel={selectionModel}
                slotProps={slotProps}
                slots={slots}
                sortingMode="server"
            />
        </>
    );
};

ListItems.propTypes = {
    filterData: PropTypes.object,
    filterItems: PropTypes.func,
    handleClickBulkItems: PropTypes.func,
    handleClickDuplicate: PropTypes.func,
    handleClickEdit: PropTypes.func,
    handleSaveRow: PropTypes.func,
    handleUpdateFilterData: PropTypes.func,
    isFetching: PropTypes.bool,
    items: PropTypes.array,
    paginationData: PropTypes.object,
    setFilterData: PropTypes.func,
    systemTypes: PropTypes.array,
};

export default ListItems;
